// PreviewLoop feedback contract — single source of truth shared by the widget and the API.

export type PinPosition = {
  /** Click coordinates relative to the viewport (clientX/clientY), in CSS
      pixels; add the scroll offsets to get page coordinates. */
  x: number;
  y: number;
  scrollX: number;
  scrollY: number;
  viewportWidth: number;
  viewportHeight: number;
};

export type BrowserInfo = {
  userAgent: string;
  os?: string;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
};

export type ConsoleLogLevel = "log" | "info" | "warn" | "error";

/** One console/JS-error entry captured on the page (extension only). */
export type ConsoleLogEntry = {
  level: ConsoleLogLevel;
  message: string;
  /** Epoch milliseconds. */
  ts: number;
};

export type FeedbackSource = "widget" | "extension";
export type ScreenshotScope = "viewport" | "fullpage";

/** Caps for the console tail attached to a Capture. Submissions beyond these are rejected. */
export const MAX_CONSOLE_ENTRIES = 100;
export const MAX_CONSOLE_BYTES = 50_000;

/** Per-screenshot upload cap. A larger screenshot part is rejected with 413. */
export const MAX_SCREENSHOT_BYTES = 8 * 1024 * 1024;
/** Room a submission may spend on JSON metadata on top of its screenshots. */
export const MAX_METADATA_BYTES = 1024 * 1024;
/** Most pins one batch submission may carry. */
export const MAX_BATCH_ITEMS = 20;
/** Largest page the tenant feedback list will return; callers asking for more are clamped. */
export const MAX_LIST_LIMIT = 100;

/** One page element linked to a Capture. The first element is its primary target. */
export type LinkedElement = {
  selector: string;
  /** Trimmed text content, if any. */
  elementText?: string;
};

/** A Capture always has at least one linked page element. */
export type LinkedElements = [LinkedElement, ...LinkedElement[]];

/**
 * The target of a Capture: its ordered, non-empty linked elements. This is the only
 * target representation past the ingest boundary. Clients built before linked
 * elements sent a single top-level `selector` / `elementText` pair instead; the API
 * still accepts that wire shape and reads it as a one-element collection.
 */
export type FeedbackTarget = {
  linkedElements: LinkedElements;
};

export type FeedbackItemBase = {
  comment: string;
  pageUrl: string;
  pin: PinPosition;
  browser: BrowserInfo;
  consoleLog?: ConsoleLogEntry[];
  source?: FeedbackSource;
  screenshotScope?: ScreenshotScope;
};

/** JSON body of the feedback submission. The screenshot PNG is sent as a separate multipart field. */
export type FeedbackPayload = FeedbackItemBase & {
  projectKey: string;
} & FeedbackTarget;

export const FEEDBACK_STATUSES = ["new", "in_progress", "resolved", "closed"] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export function isFeedbackStatus(value: unknown): value is FeedbackStatus {
  return typeof value === "string" && (FEEDBACK_STATUSES as readonly string[]).includes(value);
}

/** Where the widget loader is served from; the snippet we hand customers points here. */
export const WIDGET_LOADER_URL = "https://previewloop-widget.pages.dev/loader.js" as const;

/** The script tag a customer pastes into their preview deployment, before `</body>`. */
export function embedSnippet(publicKey: string): string {
  return [
    "<script",
    `  src="${WIDGET_LOADER_URL}"`,
    `  data-project="${publicKey}"`,
    "  defer",
    "></script>",
  ].join("\n");
}

export const API_VERSION = "v1" as const;
export const API_ORIGIN = "https://api.previewloop.com" as const;
export const HEALTH_PATH = "/api/v1/health" as const;
export const FEEDBACK_PATH = "/api/v1/feedback" as const;
export const BATCH_FEEDBACK_PATH = "/api/v1/feedback/batch" as const;
export const PROJECTS_PATH = "/api/v1/projects" as const;
/** Public, unauthenticated: what this deployment is configured to offer. */
export const CAPABILITIES_PATH = "/api/v1/capabilities" as const;

/**
 * Response of GET /api/v1/capabilities. The dashboard reads this instead of guessing
 * from its own environment, so there is one source of truth for what a deployment can do.
 */
export type Capabilities = {
  /** Present when the Worker holds GitHub App credentials; `slug` builds the install URL. */
  githubApp: { slug: string } | null;
};

/** Headers the dashboard Worker sends on service-binding calls (not for public clients). */
export const INTERNAL_KEY_HEADER = "X-PreviewLoop-Internal-Key" as const;
export const INTERNAL_ORG_HEADER = "X-PreviewLoop-Org-Id" as const;
/** Authenticated Better Auth user behind the call; the API verifies its membership itself. */
export const INTERNAL_USER_HEADER = "X-PreviewLoop-User-Id" as const;

/** Better Auth organization roles. A member row may carry several, comma separated. */
export const ORG_ROLES = ["owner", "admin", "member"] as const;

export type OrgRole = (typeof ORG_ROLES)[number];

/** Roles allowed to manage an organization's projects. */
export const ORG_ADMIN_ROLES = ["owner", "admin"] as const satisfies readonly OrgRole[];

/** Roles an invitation may grant. `owner` is never offered; it stays with the creator. */
export const INVITABLE_ROLES = ["member", "admin"] as const satisfies readonly OrgRole[];

export type InvitableRole = (typeof INVITABLE_ROLES)[number];

export function isInvitableRole(value: string): value is InvitableRole {
  return INVITABLE_ROLES.some((role) => role === value);
}

/**
 * Sends the email behind a Better Auth organization invitation. The dashboard creates the
 * invitation row; only the API Worker holds the mail credentials, so the dashboard asks it
 * to deliver the link. Admin-gated, and the API reads the row itself: the recipient, the
 * organization name and the link are never taken from the request.
 */
export const INVITATION_EMAIL_PATH = "/api/v1/invitations/email" as const;

/** Body of POST /api/v1/invitations/email. */
export type SendInvitationEmailBody = {
  /** Better Auth invitation id. Must belong to the calling organization and still be pending. */
  invitationId: string;
};

/** Dashboard path that accepts an invitation. The API builds the emailed link from it. */
export function invitePath(invitationId: string): string {
  return `/invite/${encodeURIComponent(invitationId)}`;
}

/**
 * Whether a stored `member.role` grants project administration.
 *
 * Read on both sides: the API enforces with it, the dashboard decides with it whether to
 * show a write affordance at all. One implementation, so the UI cannot promise something
 * the API will refuse.
 */
export function holdsOrgAdminRole(role: string): boolean {
  return role.split(",").some((held) => ORG_ADMIN_ROLES.some((admin) => admin === held.trim()));
}

export function feedbackDetailPath(id: string): string {
  return `/api/v1/feedback/${id}`;
}

export function screenshotAccessPath(id: string): string {
  return `/api/v1/screenshots/${id}`;
}

export function projectDetailPath(id: string): string {
  return `/api/v1/projects/${id}`;
}

/** The tracker issue a Linked Capture became (see CONTEXT.md: Linked Capture). */
export type CaptureIssueLink = {
  repo: string;
  number: number;
};

export type FeedbackListItem = {
  id: string;
  projectId: string;
  projectName: string;
  comment: string;
  pageUrl: string;
  linkedElements: LinkedElements;
  status: FeedbackStatus;
  source: FeedbackSource;
  createdAt: string;
  /** The Batch this Capture was sent in; null for pre-batch rows. */
  batchId: string | null;
  pin: PinPosition;
  screenshotScope: ScreenshotScope;
  /** Signed short-lived thumbnail URL, minted per response. */
  screenshotUrl: string | null;
  /** Present exactly when the Capture became a tracker issue (a Linked
      Capture) - repo and number always exist together, never apart. */
  issue: CaptureIssueLink | null;
};

export type FeedbackDetail = FeedbackListItem & {
  browser: BrowserInfo;
  consoleLog: ConsoleLogEntry[] | null;
  screenshotExpiresAt: string | null;
  /** Last Delivery failure, when a tracker Destination could not be reached. */
  deliveryError: string | null;
};

export type FeedbackListResponse = {
  items: FeedbackListItem[];
  nextCursor: string | null;
};

export type ProjectSummary = {
  id: string;
  name: string;
  publicKey: string;
  ownerEmail: string;
  allowedOrigins: string[];
  /**
   * Whether a Capture is still emailed to `ownerEmail` when the project has no
   * email Destination. Off means such a project sends no email at all.
   */
  ownerEmailFallback: boolean;
  createdAt: string;
  /**
   * Members granted access to this project (GH #206). Owners and admins are never listed:
   * they reach every project in the organization, so this only records who among the plain
   * members may see it. Empty means no member can.
   */
  memberUserIds: string[];
};

/** Delivery targets a Project sends Captures to. Email is the default kind. */
export type DestinationKind = "email" | "github";

export type EmailDestinationConfig = { email: string };
export type GithubDestinationConfig = { repo: string; labels?: string[] };
export type DestinationConfig = EmailDestinationConfig | GithubDestinationConfig;

/** A Destination as returned by the API. Secrets are write-only and never appear here. */
export type DestinationSummary = {
  id: string;
  kind: DestinationKind;
  config: DestinationConfig;
  /** Set when a GitHub Destination authenticates through an App installation instead of a PAT. */
  installationId: number | null;
  /** Set when delivery is paused; the row and its Captures' issue links stay. */
  disabledReason: DestinationDisabledReason | null;
  createdAt: string;
};

export type DestinationListResponse = {
  items: DestinationSummary[];
};

/**
 * Body of POST /api/v1/projects/:id/destinations. A GitHub Destination authenticates
 * with exactly one of `secret` (a PAT) or `installationId` (a connected GitHub App).
 */
export type CreateDestinationBody = {
  kind: DestinationKind;
  config: DestinationConfig;
  /** Destination credential (e.g. a GitHub PAT). Stored write-only. */
  secret?: string;
  installationId?: number;
};

/** GitHub App installations connected to the caller's organization. */
export const GITHUB_INSTALLATIONS_PATH = "/api/v1/github/installations" as const;
export function githubInstallationPath(installationId: number): string {
  return `${GITHUB_INSTALLATIONS_PATH}/${installationId}`;
}

export type GithubAccountType = "Organization" | "User";

/** An Installation as returned by the API. The cached token never appears here. */
export type GithubInstallationSummary = {
  /** GitHub's installation id. */
  id: number;
  accountLogin: string;
  accountType: GithubAccountType;
  /** Set while GitHub has the installation suspended; delivery and sweep pause. */
  suspendedAt: string | null;
  createdAt: string;
};

export type GithubInstallationListResponse = {
  items: GithubInstallationSummary[];
};

/** Repositories an Installation can reach, fetched live from GitHub. */
export function githubInstallationRepositoriesPath(installationId: number): string {
  return `${githubInstallationPath(installationId)}/repositories`;
}

export type GithubInstallationRepository = {
  /** `owner/name`. */
  fullName: string;
  private: boolean;
};

export type GithubInstallationRepositoriesResponse = {
  items: GithubInstallationRepository[];
};

/**
 * Body of POST /api/v1/github/installations. The user's GitHub OAuth token proves the
 * caller is the person who installed the App: the API checks the installation id
 * appears in that user's own installations before attaching it to the org.
 */
export type ConnectGithubInstallationBody = {
  installationId: number;
  userAccessToken: string;
};

/** Why a Destination is disabled; the row and its issue links stay. */
export type DestinationDisabledReason =
  | "installation_disconnected"
  | "installation_deleted"
  | "repository_removed";

/** Single Worker route receiving GitHub issue webhooks for every project. */
export const GITHUB_WEBHOOK_PATH = "/api/v1/webhooks/github" as const;

/**
 * Create response: for tracker kinds it carries the webhook credentials exactly once —
 * the dashboard shows them for manual repo setup and they are never readable again.
 */
export type CreateDestinationResponse = DestinationSummary & {
  webhookUrl?: string;
  webhookSecret?: string;
};

/**
 * Body of PATCH /api/v1/projects/:id/destinations/:destinationId. Credentials are
 * write-only, so the only edits are replacing the token or minting a fresh webhook
 * secret; at least one must be present.
 */
export type UpdateDestinationBody = {
  secret?: string;
  rotateWebhookSecret?: boolean;
  /**
   * Moves a PAT Destination onto a connected App installation in place: the stored PAT
   * and per-destination webhook secret are cleared and every issue link is kept.
   */
  switchToInstallationId?: number;
};

/** Like the create response: a rotated webhook secret is carried exactly once. */
export type UpdateDestinationResponse = CreateDestinationResponse;

/** A Project as listed for its org, with the Destinations it delivers to. */
export type ProjectListItem = ProjectSummary & { destinations: DestinationSummary[] };

/** `PUT /api/v1/projects/:id/members`: replaces the project's member grants whole. */
export function projectMembersPath(projectId: string): string {
  return `${projectDetailPath(projectId)}/members`;
}

export type UpdateProjectMembersBody = {
  /** Every user id that may see this project. Each must be a member of the organization. */
  userIds: string[];
};

export type ProjectListResponse = {
  items: ProjectListItem[];
};

export function projectDestinationsPath(projectId: string): string {
  return `/api/v1/projects/${projectId}/destinations`;
}

export function projectDestinationPath(projectId: string, destinationId: string): string {
  return `${projectDestinationsPath(projectId)}/${destinationId}`;
}

export type PatchFeedbackBody = {
  status: FeedbackStatus;
};

/** Body of POST /api/v1/projects. The org comes from the caller, never from the body. */
export type CreateProjectBody = {
  name: string;
  ownerEmail: string;
  allowedOrigins?: string[];
};

/**
 * Body of PATCH /api/v1/projects/:id. Every present field is replaced whole; at
 * least one must be present.
 */
export type UpdateProjectBody = {
  allowedOrigins?: string[];
  ownerEmailFallback?: boolean;
};

export type { ApiErrorBody, ApiErrorCode, FeedbackErrorBody, FeedbackErrorCode } from "./errors";
export { isApiErrorCode } from "./errors";
export { isApiErrorBody } from "./errors";

/** Multipart field names for POST /api/v1/feedback. */
export const FIELD_PAYLOAD = "payload" as const;
export const FIELD_SCREENSHOT = "screenshot" as const;

/** A single pinned comment, without the project key (which is shared across a batch). */
export type FeedbackItem = FeedbackItemBase & FeedbackTarget;

/** Body of POST /api/v1/feedback/batch — several pins submitted together. */
export type BatchFeedbackPayload = {
  projectKey: string;
  items: FeedbackItem[];
};

/** Multipart screenshot field name for item `index` in a batch (e.g. "screenshot_0"). */
export function screenshotField(index: number): string {
  return `${FIELD_SCREENSHOT}_${index}`;
}
